<template>
  <div class="content d-flex flex-column flex-column-fluid" id="kt_content">
    <div class="post d-flex flex-column-fluid" id="kt_post">
      <div id="kt_content_container" class="container-xxl">

        <ul class="nav nav-tabs nav-line-tabs nav-stretch fs-6 border-0 mb-6">
          <li class="nav-item">
            <router-link class="nav-link active" :to="{ name: 'loyalty-programs' }">{{ $t('loyalty.tab_programs') }}</router-link>
          </li>
          <li class="nav-item">
            <router-link class="nav-link" :to="{ name: 'loyalty-members' }">{{ $t('loyalty.tab_members') }}</router-link>
          </li>
        </ul>

        <div v-if="isReady" class="card">
          <div class="card-header flex-wrap border-0 pt-6 pb-0">
            <div class="card-title">
              <h3 class="card-label">{{ $t('loyalty.list') }}</h3>
            </div>
            <div class="card-toolbar">
              <a href="#" v-if="can('loyalty_program_create')" @click.prevent="toggleCreateModal" class="btn btn-sm btn-primary">{{ $t('loyalty.create') }}</a>
            </div>
          </div>
          <div class="card-body">
            <div v-if="programs.length === 0" class="text-muted text-center py-10">
              {{ $t('loyalty.empty') }}
            </div>
            <!-- Mobile : une carte par programme (le tableau à 6 colonnes débordait) -->
            <div v-if="programs.length" class="d-md-none">
              <div v-for="program in programs" :key="'m-' + program.id" class="loyalty-program-item">
                <div class="d-flex justify-content-between align-items-start gap-2">
                  <div class="fw-bolder fs-6 text-gray-800">{{ program.name }}</div>
                  <span :class="program.is_active ? 'badge badge-light-success' : 'badge badge-light-danger'">
                    {{ program.is_active ? $t('loyalty.active') : $t('loyalty.inactive') }}
                  </span>
                </div>
                <div class="text-muted fs-7 mt-1">
                  {{ earningTypeLabel(program.earning_type) }} · {{ program.rewards ? program.rewards.length : 0 }} {{ $t('loyalty.rewards_count').toLowerCase() }}
                </div>
                <div class="d-flex flex-wrap gap-1 mt-2" v-if="program.shops && program.shops.length">
                  <span v-for="shop in program.shops" :key="shop.id" class="badge badge-light-primary">{{ shop.name }}</span>
                </div>
                <div class="d-flex justify-content-end gap-2 mt-3">
                  <router-link v-if="can('loyalty_program_update')" :to="{ name: 'loyalty-program-detail', params: { programId: program.id } }" class="btn btn-sm btn-icon btn-light-primary">
                    <i class="fa fa-edit"></i>
                  </router-link>
                  <a href="#" v-if="can('loyalty_program_delete')" @click.prevent="removeProgram(program.id)" class="btn btn-sm btn-icon btn-light-danger">
                    <i class="fa fa-trash"></i>
                  </a>
                </div>
              </div>
            </div>

            <div v-if="programs.length" class="table-responsive d-none d-md-block">
            <table class="table align-middle table-row-bordered fs-6 gy-3">
              <thead>
                <tr class="text-start text-gray-400 fw-bolder fs-7 text-uppercase gs-0">
                  <th class="min-w-150px">{{ $t('loyalty.name') }}</th>
                  <th class="min-w-150px">{{ $t('loyalty.earning_type') }}</th>
                  <th class="min-w-200px">{{ $t('loyalty.shops') }}</th>
                  <th class="min-w-100px">{{ $t('loyalty.rewards_count') }}</th>
                  <th class="min-w-100px">{{ $t('loyalty.status') }}</th>
                  <th class="min-w-100px">{{ $t('action') }}</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="program in programs" :key="program.id">
                  <td>{{ program.name }}</td>
                  <td>{{ earningTypeLabel(program.earning_type) }}</td>
                  <td>
                    <span v-for="shop in program.shops" :key="shop.id" class="badge badge-light-primary me-1">{{ shop.name }}</span>
                  </td>
                  <td>{{ program.rewards ? program.rewards.length : 0 }}</td>
                  <td>
                    <span :class="program.is_active ? 'badge badge-light-success' : 'badge badge-light-danger'">
                      {{ program.is_active ? $t('loyalty.active') : $t('loyalty.inactive') }}
                    </span>
                  </td>
                  <td>
                    <router-link v-if="can('loyalty_program_update')" :to="{ name: 'loyalty-program-detail', params: { programId: program.id } }" class="btn btn-sm btn-icon btn-light-primary me-2">
                      <i class="fa fa-edit"></i>
                    </router-link>
                    <a href="#" v-if="can('loyalty_program_delete')" @click.prevent="removeProgram(program.id)" class="btn btn-sm btn-icon btn-light-danger">
                      <i class="fa fa-trash"></i>
                    </a>
                  </td>
                </tr>
              </tbody>
            </table>
            </div>
          </div>
        </div>
        <div v-else class="card">
          <Loader />
        </div>
      </div>
    </div>

    <div class="modal fade" id="loyaltyCreateModal" data-backdrop="static" tabindex="-1" role="dialog" aria-hidden="true">
      <div class="modal-dialog" role="document">
        <form @submit.prevent="createProgramData" class="form">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ $t('loyalty.new') }}</h5>
              <div class="modal-icon-close-btn ms-2" data-bs-dismiss="modal" aria-label="Close">
                <i class="fa fa-times"></i>
              </div>
            </div>
            <div class="modal-body">
              <div class="d-flex flex-column mb-8 fv-row">
                <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                  <span class="required">{{ $t('loyalty.name') }}</span>
                </label>
                <input type="text" class="form-control form-control-solid" v-model="formCreateData.name" :placeholder="$t('loyalty.name_placeholder')" required />
              </div>
              <div class="mb-8">
                <Select2 containerClass="" name="loyaltyCreateShops" :label="$t('loyalty.shops')" parentId="#loyaltyCreateModal"
                         :multiple="true" :isRequired="true" v-model="formCreateData.shop_ids">
                  <option v-for="shop in myShops" :key="shop.id" :value="shop.id">{{ shop.name }}</option>
                </Select2>
                <div class="form-text">{{ $t('loyalty.shops_hint') }}</div>
              </div>
              <div class="d-flex flex-column mb-8 fv-row">
                <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                  <span class="required">{{ $t('loyalty.earning_type') }}</span>
                </label>
                <select class="form-select form-select-solid" v-model="formCreateData.earning_type" required>
                  <option v-for="type in earningTypes" :key="type.value" :value="type.value">{{ type[locale] }}</option>
                </select>
              </div>
              <div class="d-flex flex-column mb-8 fv-row">
                <label class="d-flex align-items-center fs-6 fw-bold mb-2">
                  <span class="required">{{ earningRateLabel }}</span>
                </label>
                <input type="number" min="0.01" step="0.01" class="form-control form-control-solid" v-model.number="formCreateData.earning_rate" required />
                <div class="form-text">{{ earningRateHint }}</div>
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-light btn-light-close" data-bs-dismiss="modal">{{ $t('button.cancel') }}</button>
              <button type="submit" class="btn btn-primary" :disabled="isSaving">{{ $t('button.save') }}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import Loader from '@/components/Loader.vue'
import Select2 from '@/components/Select2.vue'
import { useHasAbility } from '@/composables/hasAbility'

const store = useStore()
const router = useRouter()
const { can } = useHasAbility()

const isReady = ref(false)
const isSaving = ref(false)
const locale = computed(() => i18n.global.locale)

const formCreateData = ref({
  name: '',
  shop_ids: [],
  earning_type: 'per_purchase',
  earning_rate: null,
})

const programs = computed(() => store.getters['loyalty/getPrograms'])
const earningTypes = computed(() => store.getters['loyalty/getEarningTypes'])
const myShops = computed(() => store.getters['loyalty/getMyShops'])

const earningRateLabel = computed(() => {
  return formCreateData.value.earning_type === 'per_amount'
    ? i18n.global.t('loyalty.earning_rate_amount_label')
    : i18n.global.t('loyalty.earning_rate_purchase_label')
})

const earningRateHint = computed(() => {
  return formCreateData.value.earning_type === 'per_amount'
    ? i18n.global.t('loyalty.earning_rate_amount_hint')
    : i18n.global.t('loyalty.earning_rate_purchase_hint')
})

function earningTypeLabel(value) {
  const found = (earningTypes.value || []).find((type) => type.value === value)
  return found ? found[locale.value] : value
}

function toggleCreateModal() {
  formCreateData.value = { name: '', shop_ids: [], earning_type: 'per_purchase', earning_rate: null }
  $('#loyaltyCreateModal').modal('toggle')
}

async function createProgramData() {
  isSaving.value = true
  try {
    const response = await store.dispatch('loyalty/createProgram', formCreateData.value)
    $('#loyaltyCreateModal').modal('hide')
    window.toastr['success'](i18n.global.t('loyalty.created'))
    router.push({ name: 'loyalty-program-detail', params: { programId: response.data.program.id } })
  } catch (err) {
    const message = err?.response?.data?.message
    window.toastr['error'](typeof message === 'string' ? message : i18n.global.t('loyalty.save_error'))
  } finally {
    isSaving.value = false
  }
}

async function removeProgram(id) {
  Swal.fire({
    title: i18n.global.t('loyalty.confirm_delete_title'),
    text: i18n.global.t('loyalty.confirm_delete_text'),
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#3085d6',
    cancelButtonColor: '#d33',
    confirmButtonText: i18n.global.t('button.yesDelete'),
    cancelButtonText: i18n.global.t('button.cancel'),
  }).then(async (result) => {
    if (result.isConfirmed) {
      await store.dispatch('loyalty/deleteProgram', id)
      window.toastr['success'](i18n.global.t('loyalty.deleted'))
    }
  })
}

onMounted(async () => {
  await Promise.all([
    store.dispatch('loyalty/loadPrograms'),
    store.dispatch('loyalty/loadEarningTypes'),
    store.dispatch('loyalty/loadMyShops'),
  ])
  isReady.value = true
})
</script>

<style scoped>
.loyalty-program-item {
  padding: 14px 0;
  border-bottom: 1px dashed #e4e6ef;
}
.loyalty-program-item:last-child {
  border-bottom: 0;
}
</style>
